import { toast } from "react-hot-toast";
import { setLoading, setToken } from "../../slices/authSlice";
import { setUser } from "../../slices/profileSlice";
import { apiConnector } from "../apiconnector";
import { endpoints } from "../apis";

const {
  SIGNUP_API,
  EMPLOYEESIGNUP_API,
  LOGIN_API,
  RESETPASSTOKEN_API,
  RESETPASSWORD_API,
  REFRESH_TOKEN_API,
  LOGOUT_API
} = endpoints;

// ------------------------
// SIGNUP
// ------------------------
export function signUp(signupData, navigate) {
  return async (dispatch) => {
    const toastId = toast.loading("Loading...");
    dispatch(setLoading(true));

    try {
      const response = await apiConnector("POST", SIGNUP_API, signupData);
      if (!response.success) throw new Error(response.message);

      toast.success("Signup Successful");
      navigate("/features", { state: { email: response.user.email } });
    } catch (error) {
      console.error("SIGNUP API ERROR:", error);
      toast.error(error.response?.data?.message || "Signup Failed");
      navigate("/signup");
    }

    dispatch(setLoading(false));
    toast.dismiss(toastId);
  };
}

// ------------------------
// EMPLOYEE SIGNUP
// ------------------------
export function employeeSignUp(signupData, navigate) {
  return async (dispatch) => {
    const toastId = toast.loading("Loading...");
    dispatch(setLoading(true));

    try {
      const response = await apiConnector("POST", EMPLOYEESIGNUP_API, signupData);
      if (!response.success) throw new Error(response.message);

      toast.success("Signup Successful");
      navigate("/login");
    } catch (error) {
      console.error("EMPLOYEE SIGNUP ERROR:", error);
      toast.error(error.response?.data?.message || "Signup Failed");
      navigate(`/signup?email=${signupData.email}`);
    }

    dispatch(setLoading(false));
    toast.dismiss(toastId);
  };
}

// ------------------------
// LOGIN
// ------------------------
export function login(email, password, navigate) {
  return async (dispatch) => {
    const toastId = toast.loading("Loading...");
    dispatch(setLoading(true));

    try {
      // Send credentials; cookies are handled automatically
      const response = await apiConnector(
        "POST",
        LOGIN_API,
        { email, password },
        { withCredentials: true }
      );

      if (!response.success) throw new Error(response.message);

      toast.success("Login Successful");

      // Save access token in Redux (in-memory)
      dispatch(setToken(response.accessToken));

      // Prepare user object with image
      const userWithImage = {
        ...response.user,
        image: response.user?.image
          ? response.user.image
          : `https://api.dicebear.com/5.x/initials/svg?seed=${response.user.firstName} ${response.user.lastName}`,
      };

      // Save user in Redux
      dispatch(setUser(userWithImage));

      // Navigate immediately using the API response user object
      switch (userWithImage.accountType) {
        case "SuperAdmin":
          navigate("/dashboard/superadmin");
          break;
        case "Admin":
          navigate("/dashboard/admin");
          break;
        case "Employee":
          navigate("/dashboard/employee");
          break;
        default:
          navigate("/features", { state: { email: userWithImage.email } });
      }

    } catch (error) {
      console.error("LOGIN API ERROR:", error);
      toast.error(error.response?.data?.message || error.message || "Login Failed");
    }

    dispatch(setLoading(false));
    toast.dismiss(toastId);
  };
}


// ------------------------
// LOGOUT
// ------------------------
export function logout(navigate) {
  return async (dispatch) => {
    try {
      await apiConnector("POST", LOGOUT_API, {}, { withCredentials: true });
      dispatch(setToken(null));
      dispatch(setUser(null));

      toast.success("Logged Out");
      navigate("/");
    } catch (error) {
      console.error("LOGOUT ERROR:", error);
      toast.error("Logout Failed");
    }
  };
}

// ------------------------
// REFRESH TOKEN
// ------------------------
export function refreshToken() {
  return async (dispatch) => {
    try {
      dispatch(setLoading(true));
      const response = await apiConnector("GET", REFRESH_TOKEN_API, {}, { withCredentials: true });

      if (response.success && response.accessToken) {
        // Save token in Redux
        dispatch(setToken(response.accessToken));

        // Save user info in Redux
        const userImage = response.user?.image
          ? response.user.image
          : `https://api.dicebear.com/5.x/initials/svg?seed=${response.user.firstName} ${response.user.lastName}`;
        dispatch(setUser({ ...response.user, image: userImage }));
      }
    } catch (error) {
      console.error("REFRESH TOKEN ERROR:", error);
      dispatch(setToken(null));
      dispatch(setUser(null));
    }
    dispatch(setLoading(false));
  };
}

// ------------------------
// PASSWORD RESET
// ------------------------
export function getPasswordResetToken(email, setEmailSent) {
  return async (dispatch) => {
    dispatch(setLoading(true));

    try {
      const response = await apiConnector("POST", RESETPASSTOKEN_API, { email });
      if (!response.success) throw new Error(response.message);

      toast.success("Reset Email Sent");
      setEmailSent(true);
    } catch (error) {
      console.error("RESET PASSWORD TOKEN ERROR:", error);
      toast.error("Failed to send email for resetting password");
    }

    dispatch(setLoading(false));
  };
}

export function resetPassword(password, confirmPassword, token, navigate) {
  return async (dispatch) => {
    dispatch(setLoading(true));

    try {
      const response = await apiConnector("POST", RESETPASSWORD_API, { password, confirmPassword, token });
      if (!response.success) throw new Error(response.message);

      toast.success("Password has been reset successfully");
      navigate("/login");
    } catch (error) {
      console.error("RESET PASSWORD ERROR:", error);
      toast.error("Unable to reset password");
    }

    dispatch(setLoading(false));
  };
}
